/**
 * Hook for fetching and managing notification counts
 */
import { useState, useEffect } from 'react';
import { getNotificationCounts } from '../../api/notifications';
import type { NotificationCounts } from '../../types/notification';

interface UseNotificationCountsResult {
  counts: NotificationCounts | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useNotificationCounts = (
  autoRefreshInterval?: number
): UseNotificationCountsResult => {
  const [counts, setCounts] = useState<NotificationCounts | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCounts = async () => {
    try {
      setLoading(true);
      const data = await getNotificationCounts();
      setCounts(data);
      setError(null);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load notification counts';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCounts();

    // Auto-refresh if interval is specified
    if (autoRefreshInterval && autoRefreshInterval > 0) {
      const intervalId = setInterval(fetchCounts, autoRefreshInterval);
      return () => clearInterval(intervalId);
    }
  }, [autoRefreshInterval]);

  return {
    counts,
    loading,
    error,
    refetch: fetchCounts,
  };
};
